import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';

interface SimpleMobileGalleryProps {
  images: string[];
  title: string;
}

export interface SimpleMobileGalleryRef {
  handleTouchStart: (e: React.TouchEvent) => void;
  handleTouchMove: (e: React.TouchEvent) => void;
  handleTouchEnd: () => void;
}

const SimpleMobileGallery = forwardRef<SimpleMobileGalleryRef, SimpleMobileGalleryProps>(({
  images,
  title
}, ref) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchCurrent, setTouchCurrent] = useState<number | null>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [axisLocked, setAxisLocked] = useState<null | 'x' | 'y'>(null);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const gestureHandledRef = useRef(false);

  // Handle touch events for swiping
  const handleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    gestureHandledRef.current = false;
    const touch = e.targetTouches[0];
    const x = touch.clientX;
    const y = touch.clientY;
    setTouchStart(x);
    setTouchCurrent(x);
    setTouchStartY(y);
    setAxisLocked(null);
    setIsDragging(false);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStart == null) return;
    e.stopPropagation();
    const touch = e.targetTouches[0];
    const x = touch.clientX;
    const y = touch.clientY;

    if (axisLocked === null && touchStartY != null) {
      const dx = Math.abs(x - touchStart);
      const dy = Math.abs(y - touchStartY);
      if (dx > 8 || dy > 8) {
        if (dx > dy) {
          setAxisLocked('x');
          setIsDragging(true);
        } else {
          setAxisLocked('y');
          setIsDragging(false);
        }
      }
    }

    if (axisLocked === 'x') {
      if (e.cancelable) e.preventDefault();
      setTouchCurrent(x);
    } else if (axisLocked === null) {
      // not yet locked, update x for potential drag preview
      setTouchCurrent(x);
    } else {
      // vertical scroll, do nothing special
      setIsDragging(false);
    }
  };
  const handleTouchEnd = () => {
    if (gestureHandledRef.current) return;
    gestureHandledRef.current = true;

    if (touchStart == null) {
      setIsDragging(false);
      setAxisLocked(null);
      return;
    }

    if (axisLocked !== 'x') {
      // Not a horizontal swipe; allow page scroll
      setIsDragging(false);
      setAxisLocked(null);
      setTouchStart(null);
      setTouchCurrent(null);
      setTouchStartY(null);
      return;
    }

    const width = containerRef.current?.clientWidth ?? 1;
    const endX = touchCurrent ?? touchStart;
    const distance = touchStart - endX;
    const threshold = Math.max(56, width * 0.22);
    const isLeftSwipe = distance > threshold;
    const isRightSwipe = distance < -threshold;

    if (isLeftSwipe && currentIndex < images.length - 1) {
      setCurrentIndex((i) => i + 1);
    } else if (isRightSwipe && currentIndex > 0) {
      setCurrentIndex((i) => i - 1);
    }

    // reset drag state
    setIsDragging(false);
    setAxisLocked(null);
    setTouchStart(null);
    setTouchCurrent(null);
    setTouchStartY(null);
  };

  // Expose touch handlers to parent via ref
  useImperativeHandle(ref, () => ({
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd
  }));

  // Handle dot navigation
  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  if (!images || images.length === 0) {
    return null;
  }

  // Live drag calculations for smooth swipe following the thumb
  const width = containerRef.current?.clientWidth ?? 1;
  const rawDrag = touchCurrent != null && touchStart != null ? touchCurrent - touchStart : 0;
  const atFirst = currentIndex === 0 && rawDrag > 0;
  const atLast = currentIndex === images.length - 1 && rawDrag < 0;
  const dragResistance = atFirst || atLast ? 0.35 : 1;
  const dragPx = isDragging ? rawDrag * dragResistance : 0;
  const trackStyle: React.CSSProperties = {
    transform: `translateX(calc(-${currentIndex * 100}% + ${dragPx}px))`
  };

  return (
    <div className="relative w-full h-full">
      <div 
        ref={containerRef}
        className="w-full h-full overflow-hidden touch-pan-x overscroll-contain select-none"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={(e) => { e.stopPropagation(); handleTouchEnd(); }}
        onTouchCancel={(e) => { e.stopPropagation(); handleTouchEnd(); }}
      >
        <div 
          className={`flex w-full h-full ${isDragging ? 'transition-none' : 'transition-transform duration-300 ease-in-out will-change-transform'}`}
          style={trackStyle}
        >
          {images.map((image, index) => (
            <div key={index} className="flex-shrink-0 w-full h-full">
              <img 
                src={image} 
                alt={`${title} - Image ${index + 1}`} 
                className="w-full h-full object-cover select-none"
                loading={index === 0 ? "eager" : "lazy"}
                onError={(e) => {
                  console.error('Image failed to load:', image);
                }}
              />
            </div>
          ))}
        </div>
      </div>
      
      {/* Dots indicator */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-30">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex 
                  ? 'bg-white' 
                  : 'bg-white/50'
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Image counter */}
      {images.length > 1 && (
        <div className="absolute top-4 right-4 bg-black/50 text-white text-xs px-2 py-1 rounded-full z-30">
          {currentIndex + 1} / {images.length}
        </div>
      )}
    </div>
  );
});

SimpleMobileGallery.displayName = 'SimpleMobileGallery';

export default SimpleMobileGallery;